import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Home, CalendarDays, ClipboardList, CreditCard, LogOut, MapPin, Trash2, Check, Bell } from "lucide-react";
import { api, errMsg, loadRazorpay } from "./api";
import { INFRASTRUCTURE, AMENITIES, LEGAL, LANDMARKS, CONTACT } from "../data";

const FACILITIES_FALLBACK = ["Cricket Ground", "Basketball Court", "Volleyball Court", "Badminton Court", "Children's Play Area"];
const START_TIMES = (() => {
  const out = [];
  for (let m = 5 * 60; m <= 18 * 60 + 30; m += 30) out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
  return out;
})();
const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const fmt = (t) => { const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; const hh = ((h + 11) % 12) + 1; return `${hh}:${String(m).padStart(2, "0")} ${ap}`; };
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function ClientDashboard({ user, onLogout, refreshUser }) {
  const [tab, setTab] = useState("home");
  const [notes, setNotes] = useState([]);

  useEffect(() => { api.get("/notifications").then((r) => setNotes(r.data.notifications || [])).catch(() => {}); }, []);

  const navs = [
    ["home", Home, "Home"], ["book", CalendarDays, "Book"],
    ["bookings", ClipboardList, "My Slots"], ["pay", CreditCard, "Pay"],
  ];

  return (
    <div className="min-h-screen bg-[var(--cream)] pb-24">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[var(--cream-3)] bg-[var(--cream)]/95 px-5 py-3.5 backdrop-blur">
        <div className="flex items-center gap-2.5">
          <img src="/nova-logo.png" alt="" className="h-8 w-auto" />
          <div className="leading-none">
            <p className="font-serif-display text-base text-[var(--ink)]">N Murthy <span className="text-[var(--gold)]">Groups</span></p>
            <p className="text-[9px] uppercase tracking-[0.3em] text-[var(--ink-soft)]">Community</p>
          </div>
        </div>
        <button onClick={onLogout} className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="community-logout">
          <LogOut size={15} /> Exit
        </button>
      </header>

      <main className="mx-auto max-w-2xl px-5 py-6">
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
            {tab === "home" && <HomeTab user={user} notes={notes} goPay={() => setTab("pay")} goBook={() => setTab("book")} />}
            {tab === "book" && <BookTab />}
            {tab === "bookings" && <BookingsTab />}
            {tab === "pay" && <PayTab user={user} refreshUser={refreshUser} />}
          </motion.div>
        </AnimatePresence>
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-30 border-t border-[var(--cream-3)] bg-[var(--cream)]/95 backdrop-blur">
        <div className="mx-auto grid max-w-2xl grid-cols-4">
          {navs.map(([id, Icon, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex flex-col items-center gap-1 py-3 text-[10px] uppercase tracking-widest transition-colors ${tab === id ? "text-[var(--gold-hover)]" : "text-[var(--ink-soft)]"}`}
              data-testid={`community-tab-${id}`}>
              <Icon size={20} /> {label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

function HomeTab({ user, notes, goPay, goBook }) {
  const pay = user.payment || {};
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Welcome back</p>
        <h1 className="mt-1 font-serif-display text-3xl text-[var(--ink)]">{user.name}</h1>
      </div>

      {notes.map((n, i) => (
        <div key={i} className={`flex items-start gap-3 rounded-sm border p-4 ${n.level === "overdue" ? "border-rose-300 bg-rose-50" : "border-amber-300 bg-amber-50"}`} data-testid="community-notification">
          <Bell size={18} className={n.level === "overdue" ? "text-rose-500" : "text-amber-500"} />
          <div className="flex-1">
            <p className="text-sm text-[var(--ink)]">{n.message}</p>
            <button onClick={goPay} className="mt-2 text-xs uppercase tracking-widest text-[var(--gold-hover)] underline">Pay now</button>
          </div>
        </div>
      ))}

      <div className="rounded-lg border border-[var(--cream-3)] bg-white p-6" data-testid="plot-card">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--ink-soft)]">Your Plot</p>
        <p className="mt-2 font-serif-display text-5xl text-[var(--ink)]">No. {user.plot_number}</p>
        {user.plot_size ? <p className="mt-1 text-[var(--gold-hover)]">{user.plot_size}</p> : null}
        <div className="mt-4 flex flex-wrap gap-2 text-xs text-[var(--ink-soft)]">
          <span className="flex items-center gap-1"><MapPin size={13} className="text-[var(--gold)]" /> Nova Royale, Bengaluru South</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button onClick={goBook} className="rounded-lg bg-[var(--ink)] p-5 text-left" data-testid="home-book-cta">
          <CalendarDays className="text-[var(--gold-bright)]" size={22} />
          <p className="mt-3 font-serif-display text-lg text-[var(--cream)]">Book a Facility</p>
          <p className="text-xs text-[var(--cream-3)]">Courts & play area</p>
        </button>
        <div className={`rounded-lg p-5 ${pay.status === "paid" ? "bg-emerald-600" : "bg-[var(--gold)]"}`}>
          <p className="text-xs uppercase tracking-widest text-black/70">Maintenance</p>
          <p className="mt-2 font-serif-display text-lg text-[#1C2427]">{pay.status === "paid" ? "Paid ✓" : "₹7000 / yr"}</p>
          <p className="text-xs text-black/70">Due {pay.due_date || "—"}</p>
        </div>
      </div>

      <Section title="Community Amenities">
        <div className="flex flex-wrap gap-2">
          {AMENITIES.map((a) => <span key={a} className="rounded-sm border border-[var(--cream-3)] bg-white px-3 py-1.5 text-sm text-[var(--ink-soft)]">{a}</span>)}
        </div>
      </Section>

      <Section title="Infrastructure">
        <div className="space-y-2">
          {INFRASTRUCTURE.map((i) => (
            <div key={i.title} className="flex items-start gap-2 text-sm">
              <Check size={15} className="mt-0.5 shrink-0 text-[var(--gold)]" />
              <span className="text-[var(--ink)]"><strong>{i.title}</strong> — <span className="text-[var(--ink-soft)]">{i.desc}</span></span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Legal & Approvals">
        <div className="flex flex-wrap gap-2">
          {LEGAL.map((l) => <span key={l} className="flex items-center gap-1.5 rounded-sm border border-[var(--cream-3)] bg-white px-3 py-1.5 text-xs text-[var(--ink-soft)]"><Check size={13} className="text-[var(--gold)]" />{l}</span>)}
        </div>
      </Section>

      <Section title="Nearby">
        <div className="divide-y divide-[var(--cream-3)]">
          {LANDMARKS.map((l) => (
            <div key={l.place} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-[var(--ink)]">{l.place}</span><span className="font-serif-display text-lg text-[var(--gold-hover)]">{l.dist}</span>
            </div>
          ))}
        </div>
      </Section>

      <p className="pt-2 text-center text-xs text-[var(--ink-soft)]">Office: {CONTACT.email}</p>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <h3 className="mb-3 font-serif-display text-xl text-[var(--ink)]">{title}</h3>
    {children}
  </div>
);

function BookTab() {
  const [facility, setFacility] = useState(FACILITIES_FALLBACK[0]);
  const [date, setDate] = useState(todayStr());
  const [start, setStart] = useState("06:00");
  const [duration, setDuration] = useState(1);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/bookings?facility=${encodeURIComponent(facility)}&date=${date}`).then((r) => setExisting(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility, date]);

  const book = async () => {
    setLoading(true);
    try {
      await api.post("/bookings", { facility, date, start_time: start, duration_hours: Number(duration) });
      toast.success(`Booked ${facility} · ${fmt(start)}`);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Could not book this slot"));
    } finally {
      setLoading(false);
    }
  };

  const endMin = toMin(start) + duration * 60;
  const validFit = endMin <= 19 * 60 + 30;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Book a Slot</p>
        <h1 className="mt-1 font-serif-display text-3xl text-[var(--ink)]">Facilities</h1>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">Open daily 5:00 AM – 7:30 PM · 1, 2 or 5 hour slots</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="facility-picker">
        {FACILITIES_FALLBACK.map((f) => (
          <button key={f} onClick={() => setFacility(f)}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${facility === f ? "border-[var(--gold)] bg-[var(--gold)] text-[#1C2427]" : "border-[var(--cream-3)] bg-white text-[var(--ink-soft)]"}`}
            data-testid={`facility-${f.replace(/\s+/g, "-").toLowerCase()}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-widest text-[var(--ink-soft)]">Date</span>
          <input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-sm border border-[var(--cream-3)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[var(--gold)]" data-testid="booking-date" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-widest text-[var(--ink-soft)]">Start Time</span>
          <select value={start} onChange={(e) => setStart(e.target.value)}
            className="w-full rounded-sm border border-[var(--cream-3)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[var(--gold)]" data-testid="booking-start">
            {START_TIMES.map((t) => <option key={t} value={t}>{fmt(t)}</option>)}
          </select>
        </label>
      </div>

      <div>
        <span className="mb-2 block text-xs uppercase tracking-widest text-[var(--ink-soft)]">Duration</span>
        <div className="flex gap-2">
          {[1, 2, 5].map((d) => (
            <button key={d} onClick={() => setDuration(d)}
              className={`flex-1 rounded-sm border py-2.5 text-sm transition-colors ${duration === d ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--cream)]" : "border-[var(--cream-3)] bg-white text-[var(--ink-soft)]"}`}
              data-testid={`duration-${d}`}>
              {d} {d === 1 ? "hour" : "hours"}
            </button>
          ))}
        </div>
      </div>

      {!validFit && <p className="text-sm text-rose-600">This duration goes past 7:30 PM. Pick an earlier start time.</p>}

      <button onClick={book} disabled={loading || !validFit}
        className="w-full rounded-sm bg-[var(--gold)] py-4 text-xs uppercase tracking-[0.25em] text-[#1C2427] transition-transform hover:-translate-y-0.5 disabled:opacity-50"
        data-testid="confirm-booking-btn">
        {loading ? "Booking…" : `Book ${fmt(start)} – ${fmt(`${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`)}`}
      </button>

      <div>
        <h3 className="mb-3 font-serif-display text-lg text-[var(--ink)]">Booked on {date}</h3>
        {existing.length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)]">No bookings yet — the whole day is free.</p>
        ) : (
          <div className="space-y-2" data-testid="existing-bookings">
            {existing.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-sm border border-[var(--cream-3)] bg-white px-4 py-3 text-sm">
                <span className="text-[var(--ink)]">{fmt(b.start_time)} – {fmt(b.end_time)}</span>
                <span className="text-xs uppercase tracking-widest text-rose-500">Booked</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BookingsTab() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const load = () => api.get("/my-bookings").then((r) => { setItems(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try { await api.delete(`/bookings/${id}`); toast.success("Booking cancelled"); load(); }
    catch { toast.error("Could not cancel"); }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Your Reservations</p>
        <h1 className="mt-1 font-serif-display text-3xl text-[var(--ink)]">My Slots</h1>
      </div>
      {!loaded ? <p className="text-[var(--ink-soft)]">Loading…</p> : items.length === 0 ? (
        <p className="text-[var(--ink-soft)]" data-testid="no-bookings">You have no bookings yet. Head to the Book tab to reserve a slot.</p>
      ) : (
        <div className="space-y-3" data-testid="my-bookings-list">
          {items.map((b) => {
            const past = new Date(`${b.date}T${b.end_time}`) < new Date();
            return (
              <div key={b.id} className="flex items-center justify-between rounded-lg border border-[var(--cream-3)] bg-white p-4">
                <div>
                  <p className="font-serif-display text-lg text-[var(--ink)]">{b.facility}</p>
                  <p className="text-sm text-[var(--ink-soft)]">{b.date} · {fmt(b.start_time)} – {fmt(b.end_time)}</p>
                </div>
                {!past && (
                  <button onClick={() => cancel(b.id)} className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-50 text-rose-500 hover:bg-rose-100" data-testid={`cancel-${b.id}`}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PayTab({ user, refreshUser }) {
  const [pay, setPay] = useState(user.payment || {});
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/payment").then((r) => { setPay(r.data); setConfigured(r.data.configured); }).catch(() => {}); }, []);

  const doPay = async () => {
    setLoading(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); return; }
      const { data } = await api.post("/payment/order");
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "N Murthy Groups", description: "Annual Maintenance Fee",
        prefill: { name: data.name, email: data.email, contact: data.phone },
        theme: { color: "#C5A028" },
        handler: async (res) => {
          try {
            const v = await api.post("/payment/verify", res);
            setPay({ ...pay, status: "paid", due_date: v.data.due_date });
            toast.success("Payment successful! Maintenance renewed.");
            refreshUser && refreshUser();
          } catch { toast.error("Payment verification failed"); }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(errMsg(err, "Payment could not be started"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Maintenance</p>
        <h1 className="mt-1 font-serif-display text-3xl text-[var(--ink)]">Annual Fee</h1>
      </div>

      <div className="rounded-lg border border-[var(--cream-3)] bg-white p-7 text-center">
        <p className="font-serif-display text-6xl text-[var(--ink)]">₹{pay.amount || 7000}</p>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">per year · community maintenance</p>
        <div className={`mt-5 inline-block rounded-full px-4 py-1.5 text-xs uppercase tracking-widest ${pay.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="payment-status">
          {pay.status === "paid" ? "Paid" : "Payment Due"}
        </div>
        <p className="mt-3 text-sm text-[var(--ink-soft)]">{pay.status === "paid" ? "Valid until" : "Due on"} <strong>{pay.due_date || "—"}</strong></p>
      </div>

      {pay.status !== "paid" && (
        configured ? (
          <button onClick={doPay} disabled={loading}
            className="w-full rounded-sm bg-[var(--gold)] py-4 text-xs uppercase tracking-[0.25em] text-[#1C2427] transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            data-testid="pay-now-btn">
            {loading ? "Opening…" : "Pay ₹7000 with Razorpay"}
          </button>
        ) : (
          <p className="rounded-sm border border-amber-300 bg-amber-50 p-4 text-center text-sm text-amber-700" data-testid="payment-not-configured">
            Online payment will be enabled once the office adds the Razorpay keys. Please contact {CONTACT.email}.
          </p>
        )
      )}
      <p className="text-center text-xs text-[var(--ink-soft)]">Secured by Razorpay. We never store your card details.</p>
    </div>
  );
}
